import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTempsReel } from '../../services/inventaireService';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeError, NdoeLoading,
} from '../../components/ndoe';
import './inventaires.css';
import './inventaireDirect.css';

/*
  SUIVI EN DIRECT — l'écran que le pilote laisse ouvert pendant que les équipes
  comptent dans le dépôt.

  TROIS PARTIS PRIS

  1. LA CADENCE, PAS SEULEMENT LE CUMUL. Un total qui monte ne dit pas si ça
     avance ENCORE. On affiche donc le rythme de la dernière heure et l'heure de
     fin qui en découle. « 40 % à 15 h » ne permet aucune décision ; « 40 %,
     80 articles/h, fini vers 17 h 20 » en permet une.

  2. LE SILENCE EST UNE INFORMATION. Une équipe qui n'a rien saisi depuis 20
     minutes est le seul événement qui demande une action immédiate — quelqu'un
     est bloqué, ou parti. C'est donc ce que l'écran met en avant, pas les
     compteurs qui tournent bien.

  3. LES DURÉES VIENNENT DU SERVEUR. Il envoie « il y a N secondes », jamais un
     horodatage à comparer à l'horloge du poste. Entre deux rafraîchissements on
     ajoute simplement le temps écoulé localement : un poste mal réglé affiche
     quand même juste.

  ⚠ Écran ADMIN. Le fil montre les quantités saisies par chacun : ouvert à un
  compteur, il casserait le comptage à l'aveugle.
*/

const RAFRAICHISSEMENT_MS = 5000;
const SILENCE_ALERTE_S = 15 * 60;

// « il y a … » à partir d'un nombre de secondes. Court exprès : la colonne est
// lue du coin de l'œil, pas déchiffrée.
function ilYA(s) {
  if (s === null || s === undefined) return 'jamais';
  if (s < 10) return "à l'instant";
  if (s < 60) return `il y a ${Math.round(s)} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  return `il y a ${h} h ${String(m % 60).padStart(2, '0')}`;
}

function duree(min) {
  if (min === null || min === undefined) return '—';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
}

// Heure de fin estimée, en heure locale du poste : c'est ce qu'on regarde pour
// décider si on tiendra avant la fermeture.
function heureDeFin(minutes) {
  if (!minutes) return null;
  const d = new Date(Date.now() + minutes * 60000);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const SOURCES = { scan: '⛶ scan', catalogue: '▦ catalogue', recherche: '⌕ recherche', manuel: '✎ saisie' };

export default function InventaireDirect() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(true);
  const [enPause, setEnPause] = useState(false);
  // Instant local du dernier rafraîchissement : sert à faire vieillir les
  // durées entre deux appels, sans comparer deux horloges.
  const recuA = useRef(Date.now());
  const [, forcer] = useState(0);

  const charger = useCallback(async (silencieux) => {
    if (!silencieux) setChargement(true);
    try {
      const { data } = await getTempsReel(id);
      setD(data);
      recuA.current = Date.now();
      setErreur('');
    } catch (e) {
      setErreur(e.response?.data?.error || 'Suivi indisponible.');
    } finally { if (!silencieux) setChargement(false); }
  }, [id]);

  useEffect(() => { charger(false); }, [charger]);

  // Rafraîchissement de fond. On s'arrête quand l'onglet passe en arrière-plan :
  // inutile d'interroger le serveur pour un écran que personne ne regarde.
  useEffect(() => {
    if (enPause) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') charger(true);
    }, RAFRAICHISSEMENT_MS);
    return () => clearInterval(t);
  }, [charger, enPause]);

  // Battement d'une seconde : fait vieillir les « il y a … » à l'écran.
  useEffect(() => {
    const t = setInterval(() => forcer((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (erreur && !d) return <NdoePage><NdoeError text={erreur} onRetry={() => charger(false)} /></NdoePage>;
  if (chargement || !d) return <NdoePage><NdoeLoading rows={6} /></NdoePage>;

  if (!d.socle) {
    return (
      <NdoePage>
        <NdoeError text="Le comptage multi-utilisateur n’est pas encore activé sur ce serveur." />
      </NdoePage>
    );
  }

  const ecoule = (Date.now() - recuA.current) / 1000;
  const vieilli = (s) => (s === null || s === undefined ? null : s + ecoule);
  const g = d.global;
  const enCours = d.inventaire.statut === 'en_cours';
  const silence = vieilli(g.depuis_dernier_geste_s);
  const enSilence = enCours && (silence === null || silence > SILENCE_ALERTE_S);
  const fin = heureDeFin(g.eta_minutes);
  const maxEquipe = Math.max(1, ...d.equipes.map((e) => e.paires));

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Stock · Inventaires"
        title={`Suivi en direct — ${d.inventaire.reference}`}
        subtitle={enCours
          ? `Actualisé toutes les ${RAFRAICHISSEMENT_MS / 1000} secondes.`
          : 'Session fermée : les chiffres ne bougent plus.'}
        actions={(
          <>
            <NdoeActionButton variant="secondary" onClick={() => setEnPause((p) => !p)}>
              {enPause ? '▶ Reprendre' : '⏸ Figer'}
            </NdoeActionButton>
            <NdoeActionButton variant="secondary" onClick={() => navigate(`/inventaires/${id}/equipe`)}>
              Équipes
            </NdoeActionButton>
            <NdoeActionButton variant="secondary" onClick={() => navigate(`/inventaires/${id}/cloturer`)}>
              Clôture
            </NdoeActionButton>
          </>
        )}
      />

      {/* --- Où en est-on globalement ---------------------------------------- */}
      <section className="ndoe-card dir-global">
        <div className="dir-jauge">
          <div className="dir-jauge__chiffre">
            <b className="num">{g.pourcentage}</b><span>%</span>
          </div>
          <div className="dir-jauge__barre"><i style={{ width: `${g.pourcentage}%` }} /></div>
          <p className="dir-jauge__sous num">
            {g.articles_a_voir - g.reste_a_voir} / {g.articles_a_voir} référence(s) avec du stock
            {g.reste_a_voir > 0 && <> · <b>{g.reste_a_voir}</b> restantes</>}
          </p>
        </div>
        <div className="dir-mesures">
          <Mesure libelle="Paires retenues" valeur={g.paires_retenues} fort />
          <Mesure libelle="Gestes" valeur={g.gestes} />
          <Mesure
            libelle={`Rythme (${g.fenetre_cadence_min} dern. min)`}
            valeur={`${g.articles_heure} réf/h`}
            note={`${g.paires_heure} paires · ${g.gestes_heure} gestes`}
          />
          <Mesure
            libelle="Fin estimée"
            valeur={fin ? `~ ${fin}` : '—'}
            note={g.eta_minutes ? `dans ${duree(g.eta_minutes)}` : (g.reste_a_voir === 0 ? 'tout est vu' : 'rythme non mesurable')}
          />
        </div>
      </section>

      {enSilence && (
        <p className="dir-silence">
          ⏸ <b>Rien n’a été saisi {ilYA(silence).replace('il y a', 'depuis')}.</b> Le comptage
          est peut-être à l’arrêt : quelqu’un est bloqué, ou la journée est finie sans que la
          session ait été clôturée.
        </p>
      )}

      {/* --- Par équipe -------------------------------------------------------- */}
      <section className="ndoe-card dir-bloc">
        <h3 className="dir-titre">Par équipe</h3>
        {!d.equipes.length && <p className="inv-aide">Personne n’est encore affecté.</p>}
        <div className="dir-equipes">
          {d.equipes.map((e) => (
            <div className={`dir-equipe${e.actifs ? '' : ' is-arret'}`} key={e.equipe || '__sans'}>
              <div className="dir-equipe__head">
                <b>{e.equipe || 'Sans équipe'}</b>
                <span className={`dir-pastille${e.actifs ? ' is-on' : ''}`}>
                  {e.actifs ? `${e.actifs} en train de compter` : 'à l’arrêt'}
                </span>
              </div>
              <div className="dir-equipe__barre">
                <i style={{ width: `${Math.round((e.paires / maxEquipe) * 100)}%` }} />
              </div>
              <p className="dir-equipe__chiffres num">
                <b>{e.paires}</b> paires · {e.gestes} geste(s) · {e.compteurs} personne(s)
              </p>
              <small>
                Dernière saisie {ilYA(vieilli(e.depuis_s))}
                {e.gestes_recents > 0 && ` · ${e.gestes_recents} geste(s) dans l’heure`}
              </small>
            </div>
          ))}
        </div>
      </section>

      {/* --- Par personne ------------------------------------------------------- */}
      <section className="ndoe-card dir-bloc">
        <h3 className="dir-titre">Par personne</h3>
        <div className="dir-gens">
          <div className="dir-gens__ligne dir-gens__ligne--head">
            <span>Compteur</span><span>Équipe</span><span>Paires</span><span>Gestes</span><span>Dernière saisie</span>
          </div>
          {d.compteurs.map((c) => (
            <div className={`dir-gens__ligne${c.actif ? ' is-actif' : ''}`} key={`${c.id_user}-${c.passe}`}>
              <span className="dir-gens__qui">
                <i className={`dir-point${c.actif ? ' is-on' : ''}`} aria-hidden="true" />
                <b>{c.username || `#${c.id_user}`}</b>
                {c.passe > 1 && <small>passe {c.passe}</small>}
              </span>
              <span className="dir-gens__eq">{c.equipe || '—'}</span>
              <span className="num">{c.paires}</span>
              <span className="num">{c.gestes}</span>
              <span className="dir-gens__quand">{ilYA(vieilli(c.depuis_s))}</span>
            </div>
          ))}
          {!d.compteurs.length && <p className="inv-aide">Aucun compteur affecté.</p>}
        </div>
      </section>

      {/* --- Le fil -------------------------------------------------------------- */}
      <section className="ndoe-card dir-bloc">
        <h3 className="dir-titre">Ce qui se passe</h3>
        <p className="inv-aide">
          Les derniers gestes, à mesure qu’ils arrivent. C’est là qu’on repère une saisie
          aberrante — 500 tapé au lieu de 50 — pendant qu’elle est encore facile à corriger.
        </p>
        <div className="dir-fil">
          {d.fil.map((f) => (
            <div className={`dir-fil__ligne${f.annule ? ' is-annule' : ''}`} key={f.id_comptage}>
              <span className="dir-fil__quand">{ilYA(vieilli(f.depuis_s))}</span>
              <span className="dir-fil__qte num">+{f.quantite}</span>
              <span className="dir-fil__art">
                <b>{f.nom_produit}</b>
                <small>
                  {f.taille ? `P${f.taille}` : ''}
                  {f.couleur ? ` · ${f.couleur}` : ''}
                  {f.code_article ? ` · ${f.code_article}` : ''}
                </small>
              </span>
              <span className="dir-fil__qui">
                {f.username}{f.equipe ? ` · ${f.equipe}` : ''}
              </span>
              <span className="dir-fil__src">{SOURCES[f.source] || f.source}</span>
            </div>
          ))}
          {!d.fil.length && <p className="inv-aide">Aucun comptage pour l’instant.</p>}
        </div>
      </section>
    </NdoePage>
  );
}

function Mesure({ libelle, valeur, note, fort }) {
  return (
    <div className={`dir-mesure${fort ? ' is-fort' : ''}`}>
      <span className="dir-mesure__lib">{libelle}</span>
      <span className="dir-mesure__val num">{valeur}</span>
      {note && <small>{note}</small>}
    </div>
  );
}
